import { USER_OFFICE } from '../../../config';
import { Session } from '../../../core/models/session';
import { Permission, Role, User } from '../models/access-control';

export interface AccessControlDb {
  permissions: Permission[];
  roles: Role[];
  users: User[];
}

const SYSTEM_ADMINISTRATOR_ROLE_ID = 1;
const DEMO_EMAIL = 'demo.user@onrsr.com.au';

export interface CallerAccount {
  username?: string;
  name?: string;
}

export class GetAccessControl {
  static permissions: Permission[] = [
    {
      id: 'occurrence.read',
      action: 'read',
      subject: 'Occurrence',
      group: 'Occurrence Review',
      label: 'View occurrences',
      description: 'Open the Awaiting Review and Reviewed lists and occurrence details.',
    },
    {
      id: 'occurrence.review',
      action: 'review',
      subject: 'Occurrence',
      group: 'Occurrence Review',
      label: 'Complete RSO review',
      description: 'Confirm or edit the RSO review of an occurrence.',
    },
    {
      id: 'occurrence.escalate',
      action: 'escalate',
      subject: 'Occurrence',
      group: 'Occurrence Review',
      label: 'Escalate to IRG',
      description: 'Escalate an occurrence to the Intelligence Review Group.',
    },
    {
      id: 'occurrence.prioritise',
      action: 'prioritise',
      subject: 'Occurrence',
      group: 'Occurrence Review',
      label: 'Set prioritisation',
      description: 'Select severity, regulatory value and prioritised follow-ups.',
    },
    {
      id: 'irg.read',
      action: 'read',
      subject: 'IrgReview',
      group: 'Intelligence Review Group',
      label: 'View IRG occurrences',
      description: 'Open the IRG list and review details.',
    },
    {
      id: 'irg.update',
      action: 'update',
      subject: 'IrgReview',
      group: 'Intelligence Review Group',
      label: 'Update IRG review',
      description: 'Edit comments, lead RSO, IRG status and serious occurrence for your office.',
    },
    {
      id: 'activity.create',
      action: 'create',
      subject: 'Activity',
      group: 'Intelligence Review Group',
      label: 'Create related activities',
      description: 'Create activities linked to an IRG occurrence.',
    },
    {
      id: 'favourites.manage',
      action: 'manage',
      subject: 'FavouriteOperator',
      group: 'Office Management',
      label: 'Manage favourite operators',
      description: 'Maintain the My Favourites operator list.',
    },
    {
      id: 'rso-office.read',
      action: 'read',
      subject: 'RsoOfficeAssignment',
      group: 'Office Management',
      label: 'View RSO office assignments',
      description: 'Open the Assign RSO Office page.',
    },
    {
      id: 'rso-office.update',
      action: 'update',
      subject: 'RsoOfficeAssignment',
      group: 'Office Management',
      label: 'Assign RSOs to offices',
      description: 'Change the office a Rail Safety Officer is assigned to.',
    },
    {
      id: 'prioritisation-config.manage',
      action: 'manage',
      subject: 'PrioritisationConfig',
      group: 'Administration',
      label: 'Manage prioritisation table',
      description: 'Open the Prioritisation Table page and change matrix cells, labels, severities and follow-up options.',
    },
    {
      id: 'history.read',
      action: 'read',
      subject: 'OccurrenceHistory',
      group: 'Administration',
      label: 'View occurrence history',
      description: 'Open the Admin History page (audit trail of occurrence changes).',
    },
    {
      id: 'access-control.manage',
      action: 'manage',
      subject: 'AccessControl',
      group: 'Administration',
      label: 'Manage roles and user access',
      description: 'Create roles, choose their permissions and assign roles to users.',
    },
    {
      id: 'session.manage',
      action: 'manage',
      subject: 'Session',
      group: 'Administration',
      label: 'Manage sessions',
      description: 'Open the Session page and expire the current session to test re-authentication.',
    },
  ];

  static roles: Role[] = [
    {
      id: SYSTEM_ADMINISTRATOR_ROLE_ID,
      name: 'System Administrator',
      description: 'Full access to every feature, including access control.',
      permissionIds: GetAccessControl.permissions.map((permission) => permission.id),
      isSystem: true,
    },
    {
      id: 2,
      name: 'Rail Safety Officer',
      description: 'Reviews, escalates and prioritises occurrences for their office.',
      permissionIds: [
        'occurrence.read',
        'occurrence.review',
        'occurrence.escalate',
        'occurrence.prioritise',
        'irg.read',
        'favourites.manage',
      ],
      isSystem: false,
    },
    {
      id: 3,
      name: 'IRG Member',
      description: 'Works escalated occurrences in the Intelligence Review Group.',
      permissionIds: [
        'occurrence.read',
        'irg.read',
        'irg.update',
        'activity.create',
      ],
      isSystem: false,
    },
    {
      id: 4,
      name: 'Office Manager',
      description: 'Maintains office assignments and favourite operators.',
      permissionIds: [
        'occurrence.read',
        'irg.read',
        'favourites.manage',
        'rso-office.read',
        'rso-office.update',
      ],
      isSystem: false,
    },
    {
      id: 6,
      name: 'Business Administrator',
      description: 'Maintains reference data and audits changes, without access control.',
      permissionIds: [
        'occurrence.read',
        'irg.read',
        'prioritisation-config.manage',
        'history.read',
        'session.manage',
      ],
      isSystem: false,
    },
    {
      id: 5,
      name: 'Read Only',
      description: 'Views occurrences and IRG items without editing.',
      permissionIds: [
        'occurrence.read',
        'irg.read',
      ],
      isSystem: false,
    },
  ];

  static users: User[] = [];

  /** Mirrors backend sign-in: match the caller by email, provisioning a first-time caller as administrator. */
  static resolveUser(db: AccessControlDb, account: CallerAccount | null): User {
    const email = account?.username ?? DEMO_EMAIL;
    const name = account?.name ?? email;
    const existing = db.users.find((user) => user.email.toLowerCase() === email.toLowerCase());
    if (existing) {
      return existing;
    }

    const provisioned: User = {
      id: Math.max(0, ...db.users.map((user) => user.id)) + 1,
      name,
      email,
      office: USER_OFFICE,
      roleIds: [SYSTEM_ADMINISTRATOR_ROLE_ID],
    };
    db.users.push(provisioned);
    return provisioned;
  }

  static buildSession(db: AccessControlDb, user: User): Session {
    const roles = db.roles.filter((role) => user.roleIds.includes(role.id));
    const permissionIds = new Set(roles.flatMap((role) => role.permissionIds));
    const granted = db.permissions.filter((permission) => permissionIds.has(permission.id));
    return {
      userId: user.id,
      name: user.name,
      email: user.email,
      office: user.office,
      roles: roles.map((role) => role.name),
      rules: granted.map(({ action, subject }) => ({ action, subject })),
    };
  }
}
